import React, { useEffect, useState } from "react";
import { getAllMentors } from "../utils/api"; 

function MentorSlide() {
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);

  // card width in px must match .cardWidth below
  const cardWidth = 288; // w-72 == 18rem == 288px
  const gapWidth = 24;

  // compute only using active mentors
  const activeMentors = mentors.filter((m) => Number(m.isActive) === 1);
  const totalWidth = activeMentors.length * (cardWidth + gapWidth);
  const animationDuration = Math.max(14, Math.round((totalWidth || (cardWidth + gapWidth) * 3) / 80)); // seconds, fallback when empty

  useEffect(() => {
    let mounted = true;

    async function fetchMentors() {
      try {
        setLoading(true);
        const data = await getAllMentors();

        // Expecting an array of mentor objects; filter only active ones
        if (!Array.isArray(data)) {
          // If API returns an object with { data: [...] } or similar, try common shapes
          const arr = data?.data || data?.mentors || [];
          if (Array.isArray(arr)) {
            if (mounted) setMentors(arr);
          } else {
            if (mounted) setMentors([]);
          }
        } else {
          if (mounted) setMentors(data);
        }
      } catch (err) {
        console.error('Failed to fetch mentors', err);
        if (mounted) setMentors([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchMentors();

    return () => {
      mounted = false;
    };
  }, []);

  // Placeholder image for missing avatars
  const placeholder = "/placeholder.svg";

  // Render nothing or a small placeholder when loading (keeps UI the same)
  return (
    <div className="w-full overflow-hidden relative py-12 pt-5">
      <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight mt-2 text-center mb-6 animate-fadeIn">
        <span className="text-[20px] md:text-5xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight">
          Meet our <span className="text-[#fd3e3d]">Mentors</span>
        </span>
      </h1>

      {/* Left fade overlay */}
      <div className="absolute inset-y-0 left-0 w-32 z-10 bg-gradient-to-r from-white to-transparent pointer-events-none" />
      {/* Right fade overlay */}
      <div className="absolute inset-y-0 right-0 w-32 z-10 bg-gradient-to-l from-white to-transparent pointer-events-none" />

      <style>{`
        @keyframes mentor-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-${totalWidth}px); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="relative">
        <div
          className="flex items-center gap-4 will-change-transform"
          style={{
            animation: `mentor-scroll ${animationDuration}s linear infinite`,
          }}
        >
          {/* Duplicate the active mentors list to create a continuous loop */}
          {[...Array(3)].map((_, copyIndex) => (
            <React.Fragment key={copyIndex}>
              {activeMentors.length === 0 && !loading ? (
                // If there are no active mentors, show 3 placeholder cards so layout remains identical
                [...Array(3)].map((__, i) => (
                  <div
                    key={`placeholder-${copyIndex}-${i}`}
                    className="flex-none bg-white/90 rounded-lg shadow-sm p-3 flex items-center"
                    style={{ width: `${cardWidth}px` }}
                  >
                    <div className="flex-shrink-0 mr-4">
                      <img src={placeholder} alt="placeholder" className="w-16 h-16 rounded-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-800">Mentor Name</h3>
                      <p className="text-sm text-gray-500 mt-1">Profession</p>
                    </div>
                  </div>
                ))
              ) : (
                activeMentors.map((m, idx) => (
                  <div
                    key={`${copyIndex}-${m._id || idx}`}
                    className="flex-none bg-white/90 rounded-xl shadow-sm p-3 flex items-center"
                    style={{ width: `${cardWidth}px` }}
                  >
                    {/* left: image (use placeholder if missing) */}
                    <div className="flex-shrink-0 mr-4">
                      <img
                        src={m.image || placeholder}
                        alt={m.name || 'Mentor'}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = placeholder;
                        }}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    </div>

                    {/* right: name + profession */}
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-800">{m.name}</h3>
                      <p className="text-sm font-medium text-gray-500 mt-1">{m.profession}</p>
                    </div>
                  </div>
                ))
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

export default MentorSlide;
